import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LayoutList, History, Ban, Printer, Trash2, Settings2, BedDouble, Search, ChevronLeft, ChevronRight, X, Send } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from './notify';
import { StageBadge } from './VisitBits';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatCATDateTime } from '../../utils/timezone';
import VisitEditor from './VisitEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisitsTable = ({ visits, showHospital, showClaim, testId, onCancel, onDelete, onManage }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="text-left p-3 font-semibold text-gray-700">Visit</th>
          <th className="text-left p-3 font-semibold text-gray-700">Patient</th>
          {showHospital && <th className="text-left p-3 font-semibold text-gray-700">Hospital</th>}
          <th className="text-left p-3 font-semibold text-gray-700">Stage</th>
          <th className="text-left p-3 font-semibold text-gray-700">Checked In</th>
          <th className="text-left p-3 font-semibold text-gray-700">{showClaim ? 'Claim' : 'Reason'}</th>
          {(onCancel || onDelete || onManage) && <th className="text-center p-3 font-semibold text-gray-700">Action</th>}
        </tr>
      </thead>
      <tbody>
        {visits.map((v) => (
          <tr key={v.visit_id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="p-3 font-medium text-gray-800">{v.visit_id}</td>
            <td className="p-3 text-gray-800">{v.patient_name}<span className="text-gray-400"> • {v.patient_serial_number}</span></td>
            {showHospital && <td className="p-3 text-gray-600">{v.hospital_name}</td>}
            <td className="p-3"><StageBadge status={v.status} /></td>
            <td className="p-3 text-gray-600">{formatCATDateTime(v.checked_in_at)}</td>
            <td className="p-3 text-gray-600">
              {showClaim ? (
                <span className="inline-flex items-center gap-2">
                  {v.billing?.claim_id || v.cancel_reason || '—'}
                  {v.external_referral?.status === 'approved' && (
                    <button
                      onClick={() => window.open(`/print-referral/${v.visit_id}`, '_blank')}
                      className="text-violet-600 hover:text-violet-800"
                      title="Print referral letter"
                      data-testid={`print-referral-${v.visit_id}`}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                  {v.admission?.discharged_at && (
                    <button
                      onClick={() => window.open(`/print-discharge/${v.visit_id}`, '_blank')}
                      className="text-rose-600 hover:text-rose-800"
                      title="Print discharge letter"
                      data-testid={`print-discharge-${v.visit_id}`}
                    >
                      <BedDouble className="w-4 h-4" />
                    </button>
                  )}
                  {v.status === 'BILLED' && (
                    <button
                      onClick={() => window.open(`/print-visit/${v.visit_id}`, '_blank')}
                      className="text-teal-600 hover:text-teal-800"
                      title="Print visit summary"
                      data-testid={`print-summary-${v.visit_id}`}
                    >
                      <Printer className="w-4 h-4" />
                    </button>
                  )}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  {v.reason || '—'}
                  {v.external_referral?.status === 'approved' && (
                    <button onClick={() => window.open(`/print-referral/${v.visit_id}`, '_blank')} className="text-violet-600 hover:text-violet-800" title="Print referral letter" data-testid={`print-referral-${v.visit_id}`}>
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                </span>
              )}
            </td>
            {(onCancel || onDelete || onManage) && (
              <td className="p-3 text-center">
                <span className="inline-flex items-center gap-3">
                  {onManage && v.status !== 'CANCELLED' && (
                    <button
                      onClick={() => onManage(v)}
                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-semibold"
                      title="Edit tests, medicines, notes or move the patient"
                      data-testid={`manage-visit-${v.visit_id}`}
                    >
                      <Settings2 className="w-3.5 h-3.5" /> Manage
                    </button>
                  )}
                  {onCancel && (
                    <button
                      onClick={() => onCancel(v)}
                      className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
                      data-testid={`cancel-visit-${v.visit_id}`}
                    >
                      <Ban className="w-3.5 h-3.5" /> Cancel
                    </button>
                  )}
                  {onDelete && (
                    <button
                      onClick={() => onDelete(v)}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-700 font-medium"
                      title="Permanently delete this visit record"
                      data-testid={`delete-visit-${v.visit_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  )}
                </span>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AllVisitsPanel({ visits, refresh, authCfg, role, isSuperAdmin }) {
  const showHospital = isSuperAdmin || role === 'ViewOnly';
  const canCancel = isSuperAdmin || ['Reception', 'Finance', 'Admin'].includes(role);
  const [history, setHistory] = useState({ visits: [], total: 0, pages: 1, hospitals: [] });
  const [dialog, setDialog] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [hospital, setHospital] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  // Debounce typing → server-side search
  useEffect(() => { const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [hospital]);

  useEffect(() => {
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (query) params.set('q', query);
    if (hospital) params.set('hospital', hospital);
    axios.get(`${API}/visits/history?${params.toString()}`, authCfg)
      .then((res) => setHistory(res.data))
      .catch(() => {});
  }, [visits.length, page, query, hospital]);

  const matches = (v) => {
    if (hospital && v.hospital_name !== hospital) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [v.patient_name, v.patient_serial_number, v.visit_id, v.family_id].some((f) => (f || '').toLowerCase().includes(q));
  };
  const openVisits = visits.filter(matches);
  const hospitalOptions = Array.from(new Set([...(history.hospitals || []), ...visits.map((v) => v.hospital_name)])).sort();

  const cancelVisit = (visit) => {
    setDialog({
      title: 'Cancel visit',
      description: `Cancel visit ${visit.visit_id} for ${visit.patient_name}? Nothing will be billed.`,
      withReason: true,
      confirmLabel: 'Cancel Visit',
      destructive: true,
      onConfirm: async (reason) => {
        try {
          await axios.post(`${API}/visits/${visit.visit_id}/cancel`, { reason: reason || '' }, authCfg);
          toast.success('Visit cancelled');
          refresh();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to cancel visit');
        }
      },
    });
  };

  const deleteVisit = (visit) => {
    setDialog({
      title: 'Delete visit permanently',
      description: `Permanently delete visit ${visit.visit_id} for ${visit.patient_name}? This removes the visit record forever and cannot be undone. Any linked claim is NOT affected.`,
      confirmLabel: 'Delete Permanently',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/visits/${visit.visit_id}`, authCfg);
          toast.success('Visit deleted permanently');
          setHistory((h) => ({ ...h, visits: (h.visits || []).filter((x) => x.visit_id !== visit.visit_id), total: Math.max(0, (h.total || 1) - 1) }));
          refresh();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to delete visit');
        }
      },
    });
  };

  if (editing) {
    return <VisitEditor visitId={editing} authCfg={authCfg} onClose={() => setEditing(null)} refresh={refresh} />;
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} {...(dialog || {})} />

      <div className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="all-visits-filters">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by patient name, serial number, visit ID or claim ID…" className="pl-9 pr-8" data-testid="all-visits-search" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear search" data-testid="all-visits-search-clear"><X className="w-4 h-4" /></button>}
        </div>
        {showHospital && (
          <select value={hospital} onChange={(e) => setHospital(e.target.value)} className="h-10 rounded-md border border-gray-300 px-3 text-sm bg-white sm:w-64" data-testid="all-visits-hospital-filter">
            <option value="">All hospitals</option>
            {hospitalOptions.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        )}
      </div>

      <div>
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2 mb-2">
          <LayoutList className="w-5 h-5 text-teal-600" /> Open Visits ({openVisits.length}{openVisits.length !== visits.length ? ` of ${visits.length}` : ''})
        </h2>
        {openVisits.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="all-visits-empty">
            {visits.length === 0 ? 'No open visits right now.' : 'No open visits match your search.'}
          </div>
        ) : (
          <VisitsTable visits={openVisits} showHospital={showHospital} testId="all-visits-table" onCancel={canCancel ? cancelVisit : null} onDelete={isSuperAdmin ? deleteVisit : null} onManage={isSuperAdmin ? (v) => setEditing(v.visit_id) : null} />
        )}
      </div>

      <div>
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2 mb-2">
          <History className="w-5 h-5 text-gray-500" /> Completed / Cancelled ({history.total})
        </h2>
        {history.visits.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500" data-testid="visit-history-empty">
            {query || hospital ? 'No completed visits match your search.' : 'No completed visits yet.'}
          </div>
        ) : (
          <>
            <VisitsTable visits={history.visits} showHospital={showHospital} showClaim testId="visit-history-table" onDelete={isSuperAdmin ? deleteVisit : null} onManage={isSuperAdmin ? (v) => setEditing(v.visit_id) : null} />
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-sm text-gray-600" data-testid="visit-history-pagination">
              <span data-testid="visit-history-page-info">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, history.total)} of {history.total}</span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="visit-history-prev"><ChevronLeft className="w-4 h-4" /> Prev</Button>
                <span className="px-2 font-medium">Page {page} / {history.pages}</span>
                <Button variant="outline" size="sm" disabled={page >= history.pages} onClick={() => setPage((p) => p + 1)} data-testid="visit-history-next">Next <ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
